import React, { useMemo, useRef, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  Scale, AlertTriangle, XCircle, CheckCircle2, RefreshCw,
  TrendingUp, TrendingDown, Sparkles, Lightbulb, ShieldAlert,
} from 'lucide-react';
import { PageHeader }   from '../components/PageHeader';
import { ChartCard }    from '../components/ChartCard';
import { Button }       from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress }     from '../components/ui/progress';
import {
  reconciliationSummary, reconciliationTrend, reconciliationAccounts,
  marginBridge, standardCostingSummary,
} from '../lib/mock-data';

const fmt = function(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
};

// Period Comparison only shows current + past periods (no future)
const PERIODS = [
  { key: 'Q2-2026', label: 'Q2 2026 (Current)' },
  { key: 'Q1-2026', label: 'Q1 2026' },
  { key: 'Q4-2025', label: 'Q4 2025' },
  { key: 'Q3-2025', label: 'Q3 2025' },
  { key: 'Q2-2025', label: 'Q2 2025' },
  { key: 'Q1-2025', label: 'Q1 2025' },
  { key: 'FY-2025', label: 'FY 2025' },
];
const FACTOR = {
  'Q2-2026': 1.0,
  'Q1-2026': 0.92,
  'Q4-2025': 0.95, 'Q3-2025': 0.93, 'Q2-2025': 0.88, 'Q1-2025': 0.84, 'FY-2025': 0.9,
};
const labelFor = function(k) {
  var p = PERIODS.find(function(p) { return p.key === k; });
  return p ? p.label : k;
};

const AI_COMMENTARY = {
  favorable: [
    'Selling price variance: +$42,000 F — price realisation improved 3.2% vs plan driven by premium product mix shift.',
    'Labour efficiency: +$18,500 F — production team exceeded standard output by 4.1%.',
    'Raw material yield: +$11,200 F — waste-reduction initiative delivered 1.8% improvement.',
  ],
  adverse: [
    'Input cost (Steel): -$38,400 U — global commodity prices rose 8.3% above standard, impacting COGS.',
    'Fixed overhead absorption: -$24,600 U — volume shortfall of 1,200 units caused under-absorption.',
    'Freight & logistics: -$9,800 U — fuel surcharges increased outbound costs by 12%.',
  ],
  redFlags: [
    '3 invoices >$50K processed without dual-approval (Ref: AP-2291, AP-2304, AP-2318) — compliance review required.',
    'Month-end cut-off: 7 journal entries posted after period close totalling $94,000 — restatement risk.',
    'Intercompany balances: $127,000 mismatch between Entity A and Entity B — unresolved.',
  ],
  improvement: [
    'Volume variance -$31,200 U: negotiate flexible capacity contracts to reduce fixed overhead at lower volumes.',
    'Procurement hedging: steel price variance could be reduced 60%+ with 6-month forward contracts.',
    'Automate AP dual-approval workflow — ~2 week implementation, 100% compliance improvement.',
  ],
};

function buildWaterfall(periodFactor, compareFactor) {
  var pf = periodFactor;
  var cf = compareFactor;
  var plannedMargin = standardCostingSummary.plannedMargin * cf;
  var actualMargin  = standardCostingSummary.actualMargin  * pf;
  var deltas = marginBridge.filter(function(d) { return d.type === 'delta'; });
  var totalDelta = deltas.reduce(function(s, d) { return s + d.value; }, 0);
  var scaledTotal = actualMargin - plannedMargin;
  var ratio = totalDelta !== 0 ? scaledTotal / totalDelta : 1;

  var scaled = [{ label: marginBridge[0].label, value: plannedMargin, type: 'anchor' }]
    .concat(deltas.map(function(d) { return { label: d.label, value: Math.round(d.value * ratio), type: 'delta' }; }))
    .concat([{ label: marginBridge[marginBridge.length - 1].label, value: actualMargin, type: 'anchor' }]);

  var cumulative = 0;
  return scaled.map(function(d) {
    if (d.type === 'anchor') {
      cumulative = d.value;
      return { label: d.label, base: 0, bar: d.value, type: 'anchor', raw: d.value };
    }
    var positive = d.value >= 0;
    var base = positive ? cumulative : cumulative + d.value;
    var bar  = Math.abs(d.value);
    cumulative += d.value;
    return { label: d.label, base: base, bar: bar, type: 'delta', positive: positive, raw: d.value };
  });
}

function WaterfallTooltip(props) {
  if (!props.active || !props.payload || !props.payload.length) return null;
  var d = props.payload[0].payload;
  var color = d.type === 'anchor' ? '#2a7d5f' : d.positive ? '#10b981' : '#ef4444';
  return (
    <div style={{ background: 'var(--color-popover)', border: '1px solid var(--color-border)', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
      <p style={{ fontWeight: 600, marginBottom: 4 }}>{props.label}</p>
      <p style={{ color: color, fontFamily: 'monospace' }}>
        {d.type === 'anchor' ? fmt(d.raw) : (d.positive ? '+' : '') + fmt(d.raw)}
      </p>
    </div>
  );
}

function WaterfallChart(props) {
  return (
    <div className="h-[280px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart key={props.runKey} data={props.data} margin={{ top: 5, right: 5, left: 0, bottom: 40 }} barCategoryGap="25%">
          <CartesianGrid stroke="var(--color-border)" vertical={false} />
          <XAxis dataKey="label" stroke="var(--color-muted-foreground)" fontSize={10}
            tickLine={false} axisLine={false} angle={-20} textAnchor="end" height={60} interval={0} />
          <YAxis stroke="var(--color-muted-foreground)" fontSize={11} tickLine={false} axisLine={false}
            tickFormatter={function(v) { return (v / 1000).toFixed(0) + 'k'; }} />
          <Tooltip content={WaterfallTooltip} />
          <Bar dataKey="base" stackId="wf" fill="transparent" isAnimationActive={false} />
          <Bar dataKey="bar" stackId="wf" radius={[4, 4, 0, 0]} isAnimationActive>
            {props.data.map(function(d, i) {
              var fill = d.type === 'anchor' ? '#2a7d5f' : d.positive ? '#10b981' : '#ef4444';
              return <Cell key={i} fill={fill} />;
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function SummaryCards(props) {
  var m = props.metrics;
  return (
    <div className="grid grid-cols-3 gap-4 mt-4">
      <div className="surface-card p-4 border-l-4 border-success/70">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="h-4 w-4 text-success" />
          <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-medium">Favorable</p>
        </div>
        <p className="font-display text-xl font-semibold text-success">{fmt(m.favorable)}</p>
        <p className="text-xs text-muted-foreground mt-1">Positive variance drivers</p>
      </div>
      <div className="surface-card p-4 border-l-4 border-destructive/70">
        <div className="flex items-center gap-2 mb-2">
          <TrendingDown className="h-4 w-4 text-destructive" />
          <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-medium">Adverse</p>
        </div>
        <p className="font-display text-xl font-semibold text-destructive">{fmt(m.adverse)}</p>
        <p className="text-xs text-muted-foreground mt-1">Negative variance drivers</p>
      </div>
      <div className={'surface-card p-4 border-l-4 ' + (m.net >= 0 ? 'border-success/70' : 'border-destructive/70')}>
        <div className="flex items-center gap-2 mb-2">
          <Scale className={'h-4 w-4 ' + (m.net >= 0 ? 'text-success' : 'text-destructive')} />
          <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-medium">Net Difference</p>
        </div>
        <p className={'font-display text-xl font-semibold ' + (m.net >= 0 ? 'text-success' : 'text-destructive')}>{fmt(m.net)}</p>
        <p className="text-xs text-muted-foreground mt-1">{m.net >= 0 ? 'Net favourable' : 'Net adverse'}</p>
      </div>
    </div>
  );
}

function AICommentaryGrid() {
  var quadrants = [
    { key: 'fav',  title: 'Favorable Variances',    icon: TrendingUp,   cls: 'border-l-4 border-success/70',     icn: 'bg-success/10 text-success',        items: AI_COMMENTARY.favorable },
    { key: 'adv',  title: 'Adverse Variances',       icon: TrendingDown, cls: 'border-l-4 border-destructive/70', icn: 'bg-destructive/10 text-destructive', items: AI_COMMENTARY.adverse },
    { key: 'red',  title: 'Red Flags — Compliance',  icon: ShieldAlert,  cls: 'border-l-4 border-warning/70',     icn: 'bg-warning/10 text-warning',        items: AI_COMMENTARY.redFlags },
    { key: 'imp',  title: 'Scope of Improvement',    icon: Lightbulb,    cls: 'border-l-4 border-primary/70',     icn: 'bg-primary/10 text-primary',        items: AI_COMMENTARY.improvement },
  ];
  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-primary" />
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">AI Commentary</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {quadrants.map(function(q) {
          var Icon = q.icon;
          return (
            <div key={q.key} className={'surface-card p-5 ' + q.cls}>
              <div className="flex items-center gap-3 mb-3">
                <span className={'grid h-8 w-8 shrink-0 place-items-center rounded-lg ' + q.icn}>
                  <Icon className="h-4 w-4" />
                </span>
                <h4 className="text-sm font-semibold">{q.title}</h4>
              </div>
              <ul className="space-y-2">
                {q.items.map(function(item, i) {
                  return (
                    <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-current shrink-0 opacity-50" />
                      {item}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatusPill(props) {
  var cfg = {
    matched:  { color: 'var(--success)',     bg: 'rgba(30,138,74,0.1)',  border: 'rgba(30,138,74,0.3)',  label: 'Matched' },
    variance: { color: 'var(--warning)',     bg: 'rgba(196,122,26,0.1)', border: 'rgba(196,122,26,0.3)', label: 'Variance' },
    missing:  { color: 'var(--destructive)', bg: 'rgba(217,79,58,0.1)',  border: 'rgba(217,79,58,0.3)',  label: 'Missing' },
  }[props.status] || { color: 'var(--muted-foreground)', bg: 'transparent', border: 'var(--border)', label: props.status };
  return (
    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, border: '1px solid ' + cfg.border, background: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export function ReconciliationPage() {
  var s = reconciliationSummary;

  // Tab 2 state — period comparison
  var [activeTab,  setActiveTab]  = useState('accuracy');
  var [basePer,    setBasePer]    = useState('Q2-2026');
  var [cmpPer,     setCmpPer]     = useState('Q1-2026');
  var [ran,        setRan]        = useState(false);
  var [activeBase, setActiveBase] = useState('Q2-2026');
  var [activeComp, setActiveComp] = useState('Q1-2026');
  var [runKey,     setRunKey]     = useState(0);
  var resultsRef = useRef(null);

  function handleRun() {
    setActiveBase(basePer);
    setActiveComp(cmpPer);
    setRan(true);
    setActiveTab('comparison');
    setRunKey(function(k) { return k + 1; });
    setTimeout(function() {
      if (resultsRef.current) {
        resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 60);
  }

  var wfData = useMemo(function() {
    return buildWaterfall(FACTOR[activeBase] || 1, FACTOR[activeComp] || 1);
  }, [activeBase, activeComp, runKey]);

  var metrics = useMemo(function() {
    var favorable = 0, adverse = 0;
    wfData.forEach(function(d) {
      if (d.type === 'delta') {
        if (d.positive) favorable += d.raw; else adverse += d.raw;
      }
    });
    return { favorable: favorable, adverse: adverse, net: favorable + adverse };
  }, [wfData]);

  var actionBtnCls = 'border border-[#009b73] bg-[#009b73] text-white shadow-sm transition-colors hover:border-[#071a2d] hover:bg-[#071a2d] hover:text-[#a7f3d0]';

  return (
    <div className="pb-12">
      <PageHeader
        eyebrow="Reconciliation Engine"
        title="Books vs Reports — Variance Intelligence"
        description={'Period accuracy and cross-period comparison · Last run ' + s.lastRun}
      />

      <section className="px-6 lg:px-10 mt-2">
        <div className="surface-card p-5 md:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5 mb-5">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Period Comparison</p>
            <h3 className="mt-1 text-base font-semibold">Select two periods and run to generate variance waterfall</h3>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Base</span>
            <Select value={basePer} onValueChange={setBasePer}>
              <SelectTrigger className="h-12 w-[245px] text-base"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PERIODS.map(function(p) { return <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>; })}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">vs</span>
            <Select value={cmpPer} onValueChange={setCmpPer}>
              <SelectTrigger className="h-12 w-[245px] text-base"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PERIODS.map(function(p) { return <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>; })}
              </SelectContent>
            </Select>
            <Button className={'h-11 px-7 ' + actionBtnCls} onClick={handleRun}>
              <RefreshCw className="h-4 w-4 mr-2" />Run
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="accuracy">Accuracy</TabsTrigger>
            <TabsTrigger value="comparison">Period Comparison</TabsTrigger>
          </TabsList>

          {/* ══════ TAB 1: PERIOD ACCURACY ══════ */}
          <TabsContent value="accuracy" className="mt-5 space-y-6">

            {/* 4 KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Match Rate',    value: s.matchRate.toFixed(1) + '%', sub: s.matched.toLocaleString() + ' of ' + s.totalRecords.toLocaleString() + ' records', icon: CheckCircle2, color: 'text-success',     progress: s.matchRate },
                { label: 'Variances',     value: s.variances,                  sub: 'Records outside tolerance',      icon: AlertTriangle, color: 'text-warning',     progress: null },
                { label: 'Missing',       value: s.missing,                    sub: 'Unmatched on either side',        icon: XCircle,       color: 'text-destructive', progress: null },
                { label: 'Net Variance',  value: fmt(s.netVariance),           sub: 'Abs: ' + fmt(s.absVariance),     icon: Scale,         color: s.netVariance < 0 ? 'text-destructive' : 'text-success', progress: null },
              ].map(function(card) {
                var Icon = card.icon;
                return (
                  <div key={card.label} className="surface-card p-5">
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{card.label}</p>
                      <Icon className={'h-4 w-4 ' + card.color} />
                    </div>
                    <p className="mt-3 font-display text-2xl font-semibold">{card.value}</p>
                    {card.progress !== null && <Progress value={card.progress} className="mt-3 h-1.5" />}
                    <p className="mt-2 text-xs text-muted-foreground">{card.sub}</p>
                  </div>
                );
              })}
            </div>

            {/* Match rate trend chart */}
            <ChartCard title="Match Rate Trend" subtitle="6-month reconciliation health" className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={reconciliationTrend} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                  <CartesianGrid stroke="var(--color-border)" vertical={false} />
                  <XAxis dataKey="month" stroke="var(--color-muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis domain={[90, 100]} stroke="var(--color-muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ background: 'var(--color-popover)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 11 }} />
                  <Line type="monotone" dataKey="matchRate" stroke="#2a7d5f" strokeWidth={2.5} dot={{ r: 3, fill: '#2a7d5f' }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Account reconciliation table */}
            <div className="surface-card overflow-hidden">
              <div className="px-5 py-3 border-b border-border/60">
                <h3 className="text-sm font-semibold">Account Reconciliation</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Books vs Reports — line by line</p>
              </div>
              <div className="grid grid-cols-12 gap-3 px-5 py-2 border-b border-border/60 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                <div className="col-span-5">Account</div>
                <div className="col-span-2 text-right">Books</div>
                <div className="col-span-2 text-right">Reports</div>
                <div className="col-span-2 text-right">Variance</div>
                <div className="col-span-1 text-right">Status</div>
              </div>
              <div className="divide-y divide-border/60">
                {reconciliationAccounts.map(function(row) {
                  return (
                    <div key={row.account} className="grid grid-cols-12 gap-3 px-5 py-3 items-center text-sm hover:bg-muted/40 transition-colors">
                      <div className="col-span-5 flex items-center gap-2">
                        {row.status === 'matched'
                          ? <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
                          : row.status === 'variance'
                            ? <AlertTriangle className="h-4 w-4 text-warning shrink-0" />
                            : <XCircle className="h-4 w-4 text-destructive shrink-0" />}
                        <span className="font-medium text-sm">{row.account}</span>
                      </div>
                      <div className="col-span-2 text-right font-mono text-xs">{fmt(row.books)}</div>
                      <div className="col-span-2 text-right font-mono text-xs">{fmt(row.report)}</div>
                      <div className={'col-span-2 text-right font-mono text-xs ' + (row.variance === 0 ? 'text-muted-foreground' : row.variance > 0 ? 'text-success' : 'text-destructive')}>
                        {row.variance === 0 ? '-' : fmt(row.variance)}
                      </div>
                      <div className="col-span-1 flex justify-end"><StatusPill status={row.status} /></div>
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* ══════ TAB 2: PERIOD COMPARISON ══════ */}
          <TabsContent value="comparison" className="mt-5 space-y-6">
            {!ran && (
              <p className="text-sm text-muted-foreground text-center py-16">
                Select periods above and click <strong>Run</strong> to generate the variance comparison.
              </p>
            )}

            {ran && (
              <div ref={resultsRef} className="surface-card p-5 space-y-0">
                {/* Chart header */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                  <div>
                    <h3 className="text-sm font-semibold">Margin Bridge — {labelFor(activeComp)} to {labelFor(activeBase)}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Waterfall of variance drivers between the two periods</p>
                  </div>
                  <div className="flex items-center gap-4 text-[11px]">
                    <span className="text-muted-foreground">
                      {labelFor(activeComp)}: <span className="font-mono font-medium text-foreground">{wfData[0] && fmt(wfData[0].raw)}</span>
                    </span>
                    <span className="text-muted-foreground">
                      {labelFor(activeBase)}: <span className="font-mono font-medium text-foreground">{wfData[wfData.length - 1] && fmt(wfData[wfData.length - 1].raw)}</span>
                    </span>
                  </div>
                </div>

                {/* Legend */}
                <div className="flex items-center gap-4 mb-3 text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: '#2a7d5f', display: 'inline-block' }} />Anchor
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: '#10b981', display: 'inline-block' }} />Favorable
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: '#ef4444', display: 'inline-block' }} />Adverse
                  </span>
                </div>

                <WaterfallChart data={wfData} runKey={runKey} />
                <SummaryCards metrics={metrics} />
                <AICommentaryGrid />
              </div>
            )}
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}
